// The answers to an agent's subject tools (view, comments, files, diff,
// commits, checks; see docs/remote-relay.md, "Subject tools"), read with
// the app's GitHub client at `background` priority, so an agent's reads
// wait behind what the person is looking at. Changeling reads pull
// requests, compares and branches' histories; a tool a subject has no facet
// for (comments on a compare or a branch) is refused by the grant
// (relay-grants.ts).
//
// Grants: a query is checked against them before anything is read, and
// again once the reads are done, right before it is answered, so a grant
// revoked or narrowed meanwhile is not answered. The GitHub paths are the
// grant's own subject.
//
// Ranges: files, diff and commits take `from` and `to`, checks `to`:
// full SHAs of points on the subject's track, `from` before `to`. A pull
// request's track is its merge base and its current commits
// (pulls/:n/commits); a compare's is its merge base and the commits the
// compare holds (the newest MAX_COMPARE_COMMITS, read as its page reads
// them, state/compare/fetch.ts). A branch's (a ref's) is bounded, so an
// agent granted one branch can page no further: the newest
// MAX_RELAY_REF_COMMITS commits of its history from the ref's tip (of the
// commits touching its path, for a history filtered to one), as its page
// reads them (state/ref/fetch.ts, every window from one tip), and their
// base, the first parent of the oldest (git's empty tree at the root): a
// `from` or `to` must be one of those, never a commit of another branch
// or older than the bound. A range defaults to the merge base (a branch's
// base) and the head; files and diff of a range read GitHub's compare of
// the two (which for two points of one lane is the diff from one to the
// other; a branch's from its base on the empty tree, the head's tree,
// every file added), a branch's only of the files in its path.
// Every answer names the `subject`, the `head_sha` the subject had when
// read (a branch's tip), and, for a range, its `from` and `to`. Commits
// replaced by a force-push are not on the track here yet; a branch's
// `commits` names its force-pushes, as far back as GitHub keeps them.
//
// Untrusted content: every string in an answer is data, never
// instructions. Fields named `untrusted_*` hold free-form text chosen by
// third parties: titles, bodies, comments, reviews, the diff, label names
// and descriptions, file paths, the base and head branches' names,
// check-run, workflow and app names, status contexts and descriptions,
// and check and status URLs (set by whoever runs the check). The agent's
// skill says to quote them, never follow them.
//
// Size: an answer's JSON stays under MAX_ANSWER_BYTES. comments, files,
// commits and checks answer a page of up to `limit` items or
// PAGE_BYTES, whichever comes first. diff answers at most DIFF_BYTES of
// the unified diff (UTF-8 bytes), less where JSON escaping would pass
// PAGE_BYTES. Each answer that has more to follow carries `next_cursor`
// (null on the last page), which the next call passes as `cursor`.
//
// Cursors: `<tool>.<snapshot>.<position>`, matching TOKEN:
//   <tool>      c (comments), f (files), k (checks), d (diff), m (commits)
//   <snapshot>  12 lowercase hex digits: the snapshot the page is cut from
//   <position>  base 36: the next item (diff: the next byte)
// The relay worker reads the snapshot with `snapshotOf` to send a follow-up
// to the tab that holds it.
//
// Snapshots: a first page (no cursor) reads GitHub. Once it is answered
// (size, grants, abort and account checked), it replaces any snapshot of
// the same connection, subject, tool, range and diff path, and when more
// follows it, what it read is kept in this tab as a snapshot. Later pages
// are cut from it: no request, nothing skipped or repeated. Once its last
// page is served it is finished: still there for a retry of that page,
// but not counted per connection, and the first to go to make room. A
// paged listing is kept item by item as UTF-8 JSON, the diff as its UTF-8
// bytes, so what a snapshot holds is counted exactly. A snapshot lives
// SNAPSHOT_LIMITS.idleMs after its last use, SNAPSHOT_LIMITS.maxAgeMs at
// most; every query first drops those past either. Snapshots are bounded
// in bytes (`totalBytes`; one over `entryBytes` is not kept, and its first
// page is answered as all there is, `truncated` with a note) and in
// unfinished ones per connection (`perConnection`). To make room, finished
// snapshots go first, then the connection holding the most bytes gives up
// its least recently used snapshot; the one just made is never given up.
// A cursor whose snapshot went idle, reached its age, was replaced, or was
// given up once finished is refused with an error saying so; one this tab
// does not hold, or that another connection held, with a generic one. Each
// says to start again from the first page. `dropSnapshots` drops a
// connection's (on revoke and unpair); all are dropped when the signed-in
// account changes, and a query read across that change fails.
//
// comments reads as it pages: its snapshot (a CommentsFeed) is made by
// the first page and read into a GitHub page at a time, only as far as the
// page being answered needs, so a first page needs the newest timeline page
// alone when that holds enough. Until its first page is answered it is a
// draft, which a first page asked again (after the first ran out of time)
// goes on from, when it reaches this same tab: a draft lives in the tab that
// read it, and the worker hands first pages to tabs in turn. A read under way when the answer has to go (`hurry`, or the
// query aborted) goes on into the snapshot for the next page; that page then
// answers with what has been read. The listing is the conversation, newest
// first, then the review threads in GitHub's order, each with its comments.
//
// GraphQL budget: every GraphQL page is first asked of `spend`, which the
// tab takes from the relay worker, where one budget counts every tab's pages
// per pairing and in all (api/relay-budget.ts). A paged listing it refuses
// answers what it has read; anything else fails, saying when to ask again.
import { graphql, rest, restAll, type CallOptions } from './api/gh';
import { LruMap } from './api/cache';
import { identityGeneration, onIdentityChange } from './api/identity';
import { BudgetSpent } from './api/relay-budget';
import { readChecks, readCommentsPage, readPrHeader, readThreadsPage, readTimelinePage } from './api/answers';
import { PR_CHECKS, PR_HEADER_QUERY, PR_THREADS_PAGE, PR_TIMELINE_PAGE, THREAD_COMMENTS_PAGE, type Actor, type CheckContext, type CheckContexts, type PrFile, type ReviewThread, type ThreadComment, type TimelineItem } from './api/queries';
import type { Priority } from './api/scheduler';
import { advances, allContexts, commitRollup, fetchFiles, FILES_TRIES, MAX_COMMENT_PAGES, MAX_FILES, MAX_THREAD_PAGES, MAX_TIMELINE_PAGES } from './state/pr/fetch';
import { fileComplexity, type AgentFileComplexity } from './state/complexityRegistry';

// The relay's types, shared with the grants the browser holds (api/relay-grants.ts).
import { authorize, refusal, type RelayCategory, type RelayGrant, type RelayQuery, type RelayTool } from './api/relay-grants';
import { KIND_NAMES, matchKey, parseSubject, type SubjectKey } from './api/relaySubject';
import type { CompareSubject, PullSubject, RefSubject, Subject } from './subject';
import { historyLane } from './logic/refTrack';
import { fetchForcePushes, fetchWindow, refKind, RefNotFound, REF_PAGE, windowFiles, type RefKind } from './state/ref/fetch';
import { session } from './state/session.svelte';
import { CompareNotFound, compareSpec, headPulls, loadCompare, pullsFrom, type CompareCommit, type LoadedCompare } from './state/compare/fetch';
export type { RelayCategory, RelayGrant, RelayQuery, RelayTool };

/** A pull request, as GitHub is asked about it. */
interface PullAt { owner: string; repo: string; number: number }

/** What an agent's reads are worth to the scheduler: behind what is on screen. */
export const PRIORITY: Priority = 'background';
/** Items per page, at most. */
export const MAX_LIMIT = 100;
/** A page of items stops before its items' JSON passes this. */
export const PAGE_BYTES = 768 * 1024;
/** diff's chunk of the diff, at most. */
export const DIFF_BYTES = 512 * 1024;
/** No answer's JSON reaches this (the relay's wire limit). */
export const MAX_ANSWER_BYTES = 1024 * 1024;

const MiB = 1024 * 1024;
/** How snapshots are bounded (see the file comment). Tests narrow them. */
export const SNAPSHOT_LIMITS = {
  idleMs: 5 * 60_000,
  maxAgeMs: 30 * 60_000,
  totalBytes: 48 * MiB,
  entryBytes: 24 * MiB,
  perConnection: 16,
};
/** What a first page too large to keep says. */
export const TOO_LARGE_NOTE = 'This listing is too large to page through: this is all of it that can be read.';

/** The arguments each tool takes. */
export const TOOL_ARGS: Record<RelayTool, readonly string[]> = {
  view: [],
  comments: ['cursor', 'limit'],
  files: ['from', 'to', 'cursor', 'limit'],
  diff: ['from', 'to', 'path', 'cursor'],
  commits: ['from', 'to', 'cursor', 'limit'],
  checks: ['to', 'cursor', 'limit'],
};

const DEFAULT_LIMIT: Partial<Record<RelayTool, number>> = { comments: 50, files: 100, checks: 100, commits: 100 };
/** The letter a tool's cursors start with. */
const CURSOR_TAG: Partial<Record<RelayTool, string>> = { comments: 'c', files: 'f', checks: 'k', diff: 'd', commits: 'm' };

/** A cursor (see the file comment): tool letter, snapshot ID, position. */
export const TOKEN = /^([cfkdm])\.([0-9a-f]{12})\.([0-9a-z]{1,8})$/;
/** A commit, as `from` and `to` name one: a full SHA (40 hex digits, or 64 in a SHA-256 repository). */
const SHA = /^(?:[0-9a-f]{40}|[0-9a-f]{64})$/;

/** The snapshot a query's cursor is cut from; null for a first page, or a cursor that is not this tool's. */
export function snapshotOf(q: RelayQuery): string | null {
  const cursor = (q?.args as Record<string, unknown> | undefined)?.cursor;
  const m = typeof cursor === 'string' ? TOKEN.exec(cursor) : null;
  return m && m[1] === CURSOR_TAG[q.tool] ? m[2] : null;
}

/** The grant that allows the query, or why none does: relay-grants.ts's one rule (`authorize`). */
function grantFor(q: RelayQuery, grants: readonly RelayGrant[]): RelayGrant | string {
  const a = authorize(grants, q.connection, q.tool, q.subject);
  return a.ok ? a.grant : a.why;
}

/** Null when `grants` allow the query; otherwise why not, naming the subject or the category it lacks. */
export function allowed(q: RelayQuery, grants: readonly RelayGrant[]): string | null {
  return refusal(grants, q.connection, q.tool, q.subject);
}

/** The subject `key` names, which GitHub is asked about. */
function readableOf(key: SubjectKey): Subject {
  const s = parseSubject(key);
  if (!s) throw new Error('The query names no subject.');
  return s;
}

/**
 * The most commits of a branch's history a query reads, and its `from`
 * and `to` may name: the newest, from the ref's tip (see the file comment).
 */
export const MAX_RELAY_REF_COMMITS = 3 * REF_PAGE;

interface Args {
  /** The snapshot and position a cursor names; null for a first page. */
  cursor: { id: string; at: number } | null;
  limit: number;
  path: string | null;
  /** The range's ends as given (lowercased); null where not given. */
  from: string | null;
  to: string | null;
}

/** The query's arguments, checked: unknown names, and values of the wrong kind or out of range, throw. Null counts as not given. */
function readArgs(q: RelayQuery): Args {
  const args = q.args ?? {};
  if (typeof args !== 'object' || Array.isArray(args)) throw new Error('Arguments must be an object.');
  const known = TOOL_ARGS[q.tool];
  for (const k of Object.keys(args)) if (!known.includes(k)) throw new Error(`${q.tool} takes no argument '${k}'.`);
  const given = (k: string) => args[k] !== undefined && args[k] !== null;
  const out: Args = { cursor: null, limit: DEFAULT_LIMIT[q.tool] ?? 0, path: null, from: null, to: null };
  if (given('limit')) {
    const l = args.limit;
    if (typeof l !== 'number' || !Number.isInteger(l) || l < 1 || l > MAX_LIMIT) throw new Error(`limit must be a whole number from 1 to ${MAX_LIMIT}.`);
    out.limit = l;
  }
  if (given('cursor')) out.cursor = readCursor(args.cursor, q.tool);
  if (given('path')) {
    const p = args.path;
    if (typeof p !== 'string' || p === '' || p.length > 4096) throw new Error('path must be a file path of the pull request.');
    out.path = p;
  }
  for (const end of ['from', 'to'] as const) if (given(end)) {
    const v = args[end];
    if (typeof v !== 'string' || !SHA.test(v.toLowerCase())) throw new Error(`${end} must be a full commit SHA.`);
    out[end] = v.toLowerCase();
  }
  return out;
}
/** What a snapshot's range is told by: the ends as given, so a cursor goes on only with the arguments its first page had. */
const rangeKey = (a: Args): string | null => (a.from === null && a.to === null ? null : `${a.from ?? ''}..${a.to ?? ''}`);

function readCursor(cursor: unknown, tool: RelayTool): { id: string; at: number } {
  const m = typeof cursor === 'string' ? TOKEN.exec(cursor) : null;
  if (!m) throw new Error(`cursor is not one ${tool} returned.`);
  if (m[1] !== CURSOR_TAG[tool]) throw new Error(`cursor is from another tool, not ${tool}.`);
  return { id: m[2], at: parseInt(m[3], 36) };
}

// ---------------------------------------------------------------- snapshots

interface Snapshot {
  id: string;
  connection: string;
  /** The subject, as `matchKey` writes it. */
  subject: string;
  /** Connection, subject and tool: what a cursor must be used with. */
  key: string;
  /** diff's path (null: the whole diff). */
  path: string | null;
  /** The range it was read for, as given (`rangeKey`); null for none. */
  range: string | null;
  data: unknown;
  bytes: number;
  created: number;
  used: number;
  /**
   * `draft`: a comments listing being read for a first page not answered
   * yet, which no cursor names. `paging`: pages of it have been answered.
   * `finished`: its last page has been served, kept for a retry of it, but
   * not counted per connection, and given up first to make room.
   */
  stage: 'draft' | 'paging' | 'finished';
}

/** Why a snapshot is gone: idle too long, past its age, replaced by a newer first page, or given up to make room once read to its end. */
type Gone = 'idle' | 'aged' | 'replaced' | 'finished';

/** By ID, least recently used first. */
const snapshots = new Map<string, Snapshot>();
/** Why a snapshot no longer here went, and whose it was: told to a cursor of that connection that names it. */
const gone = new LruMap<string, { why: Gone; connection: string }>(256);
let held = 0;

onIdentityChange(clearSnapshots);

/** Drop every snapshot (the signed-in account changed; tests). */
export function clearSnapshots(): void {
  for (const s of snapshots.values()) if (s.data instanceof CommentsFeed) s.data.abort.abort();
  snapshots.clear();
  gone.clear();
  held = 0;
}

/** Drop the snapshots of a connection, or of one of its subjects: when it is unpaired or a grant is revoked. */
export function dropSnapshots(connection: string, subject?: SubjectKey): void {
  const which = subject && matchKey(subject);
  for (const s of [...snapshots.values()]) if (s.connection === connection && (!which || s.subject === which)) drop(s, null);
}

/** How many snapshots this tab holds, and their bytes. For tests. */
export function snapshotStats(): { count: number; bytes: number } {
  return { count: snapshots.size, bytes: held };
}

function drop(s: Snapshot, why: Gone | null): void {
  if (!snapshots.delete(s.id)) return;
  // A read under way into it stops; what it brings is not kept.
  if (s.data instanceof CommentsFeed) s.data.abort.abort();
  held -= s.bytes;
  if (why) gone.set(s.id, { why, connection: s.connection });
}

/** Whether `s` is past its idle time or its age at `now`. */
function expiry(s: Snapshot, now: number): 'idle' | 'aged' | null {
  if (now - s.created >= SNAPSHOT_LIMITS.maxAgeMs) return 'aged';
  return now - s.used >= SNAPSHOT_LIMITS.idleMs ? 'idle' : null;
}

/** Drop every snapshot expired at `now`. */
function sweep(now: number): void {
  for (const s of [...snapshots.values()]) {
    const why = expiry(s, now);
    if (why) drop(s, why);
  }
}

function newId(): string {
  const b = crypto.getRandomValues(new Uint8Array(6));
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}

const keyOf = (q: RelayQuery, at: SubjectKey) => [q.connection, matchKey(at), q.tool].join('\n');

/** Drop the snapshots a first page read again replaces: the same connection, subject, tool, range and path, but `keep`. */
function replace(key: string, path: string | null, range: string | null, keep?: Snapshot): void {
  for (const old of [...snapshots.values()]) if (old !== keep && old.key === key && old.path === path && old.range === range) drop(old, 'replaced');
}

/**
 * Keep `s`, making room: past `perConnection` unfinished snapshots, the
 * connection's oldest unfinished one goes; past `totalBytes`, finished
 * snapshots go first (least recently used first), then the least recently
 * used of the connection holding the most bytes. `s` itself stays: its
 * page is being answered with a cursor into it.
 */
function keep(s: Snapshot): void {
  snapshots.set(s.id, s);
  held += s.bytes;
  room(s);
}
/** Makes room for `s`, which is kept: see `keep`. */
function room(s: Snapshot): void {
  const mine = [...snapshots.values()].filter((x) => x.connection === s.connection && x.stage !== 'finished' && x !== s);
  for (let i = 0; mine.length + 1 - i > SNAPSHOT_LIMITS.perConnection; i++) drop(mine[i], null);
  while (held > SNAPSHOT_LIMITS.totalBytes && snapshots.size > 1) {
    const others = [...snapshots.values()].filter((x) => x !== s);
    const done = others.find((x) => x.stage === 'finished');
    if (done) {
      drop(done, 'finished');
      continue;
    }
    const by = new Map<string, number>();
    for (const x of snapshots.values()) by.set(x.connection, (by.get(x.connection) ?? 0) + x.bytes);
    const order = [...by].sort((a, b) => b[1] - a[1]).map(([conn]) => conn);
    const victim = order.map((conn) => others.find((x) => x.connection === conn)).find((x) => x !== undefined);
    if (!victim) break;
    drop(victim, null);
  }
}

const restart = (tool: RelayTool) => `Call ${tool} again without a cursor to start from the first page.`;
const minutes = (ms: number) => `${ms / 60_000} minutes`;

/** The error for a cursor whose snapshot is gone, saying why. */
function goneError(tool: RelayTool, why: Gone | undefined): Error {
  const said: Record<Gone, string> = {
    idle: `This ${tool} listing has expired: it went unused for ${minutes(SNAPSHOT_LIMITS.idleMs)}.`,
    aged: `This ${tool} listing has expired: listings are kept ${minutes(SNAPSHOT_LIMITS.maxAgeMs)} at most.`,
    replaced: `This ${tool} listing was replaced: its first page was read again.`,
    finished: `This ${tool} listing was read to its last page and has since been dropped to make room.`,
  };
  return new Error(`${why ? said[why] : `This Changeling tab holds no such ${tool} listing (it was dropped to make room, or another tab made it).`} ${restart(tool)}`);
}

/** The snapshot a cursor names, used now; or the error saying why it cannot be. */
function lookup(c: Ctx, id: string): Snapshot {
  const tool = c.q.tool;
  const s = snapshots.get(id);
  if (!s) {
    const g = gone.get(id);
    throw goneError(tool, g?.connection === c.q.connection ? g.why : undefined);
  }
  if (s.connection !== c.q.connection || s.stage === 'draft') throw goneError(tool, undefined);
  if (s.key !== keyOf(c.q, c.key)) throw new Error(`cursor is not one ${tool} returned for ${c.key}.`);
  if (c.args.path !== null && c.args.path !== s.path) throw new Error(`cursor is for ${s.path === null ? 'the whole diff' : JSON.stringify(s.path)}, not ${JSON.stringify(c.args.path)}.`);
  if (rangeKey(c.args) !== s.range) throw new Error(`cursor is for ${s.range === null ? 'the whole subject' : 'another range'}: call ${tool} with the from and to of its first page.`);
  const now = c.now();
  const why = expiry(s, now);
  if (why) {
    drop(s, why);
    throw goneError(tool, why);
  }
  s.used = now;
  snapshots.delete(id);
  snapshots.set(id, s);
  return s;
}

/**
 * For a later page, the snapshot its cursor names (`c.hit`). For a first
 * page, what `read` reads, as a snapshot to keep once the page is answered
 * with more to follow (`c.keep`); `id` null when it is too large to keep
 * (`size` bytes over `entryBytes`).
 */
async function snapshot<T>(c: Ctx, read: () => Promise<T>, size: (data: T) => number): Promise<{ id: string | null; data: T; path: string | null }> {
  if (c.args.cursor) {
    const s = lookup(c, c.args.cursor.id);
    c.hit = s;
    return { id: s.id, data: s.data as T, path: s.path };
  }
  const data = await read();
  const bytes = size(data);
  if (bytes > SNAPSHOT_LIMITS.entryBytes) return { id: null, data, path: c.args.path };
  const now = c.now();
  const s: Snapshot = { id: newId(), connection: c.q.connection, subject: matchKey(c.key), key: keyOf(c.q, c.key), path: c.args.path, range: rangeKey(c.args), data, bytes, created: now, used: now, stage: 'paging' };
  c.keep = s;
  return { id: s.id, data, path: s.path };
}

/** A paged listing as a snapshot keeps it: each item as its JSON in UTF-8, and what is said of the whole. */
interface Listing<M> {
  items: Uint8Array[];
  meta: M;
}

function listing<M>(items: unknown[], meta: M): Listing<M> {
  return { items: items.map((i) => utf8.encode(JSON.stringify(i))), meta };
}

const listingBytes = (l: Listing<unknown>) => l.items.reduce((n, b) => n + b.length, 0) + bytes(l.meta);

// ---------------------------------------------------------------- answering

const utf8 = new TextEncoder();
const utf8Text = new TextDecoder();
const bytes = (v: unknown) => utf8.encode(JSON.stringify(v)).length;

/** What each tool works with: the query, the granted pull request, its arguments, how its reads are scheduled. */
interface Ctx {
  q: RelayQuery;
  /** The grant's subject, as the grant spells it: what answers name. */
  key: SubjectKey;
  /** The subject GitHub is asked about (what the open Files view's figures are kept under too). */
  subject: Subject;
  /** A compare's one read for this query (`compareOf`), shared by what the answer reads of it. */
  compare?: Promise<LoadedCompare>;
  /** A branch's one read for this query (`refOf`). */
  ref?: Promise<RefRead>;
  args: Args;
  opts: CallOptions & { priority: Priority; signal: AbortSignal };
  now: () => number;
  /** Aborts when a paged answer is to go with what has been read. */
  hurry: AbortSignal;
  /** Asks before each GraphQL page; rejects with BudgetSpent when the budget says no. */
  spend: () => Promise<void>;
  /** A first page's snapshot, kept once the page is answered with more to follow. */
  keep?: Snapshot;
  /** The snapshot a later page was cut from, marked finished once its last page is answered. */
  hit?: Snapshot;
}

/**
 * How an answer is read: `signal` aborts its reads; `now` is the clock
 * snapshots expire and the GraphQL budget is counted by; once `hurry`
 * aborts, a comments page answers with what has been read.
 */
export interface AnswerDeps {
  signal?: AbortSignal;
  now?: () => number;
  hurry?: AbortSignal;
  /** Asked before each GraphQL page (the relay worker's budget); rejects with BudgetSpent to refuse it. */
  spend: () => Promise<void>;
}

/**
 * The answer to an agent's query, read from GitHub. `grants` is asked for
 * the grants in force before the reads and again right before answering;
 * snapshots are kept and dropped only then. Throws when the grants do not
 * allow the query, when its arguments are wrong, when a cursor's snapshot
 * cannot be used, when the signed-in account changed during the reads,
 * when the subject is not a pull request, and when GitHub does not show it.
 */
export async function answerQuery(q: RelayQuery, grants: () => readonly RelayGrant[] | Promise<readonly RelayGrant[]>, deps: AnswerDeps): Promise<unknown> {
  const now = deps.now ?? Date.now;
  sweep(now());
  const grant = grantFor(q, await grants());
  if (typeof grant === 'string') throw new Error(grant);
  const subject = readableOf(grant.subject);
  const args = readArgs(q);
  const signal = deps.signal ?? new AbortController().signal;
  const generation = identityGeneration();
  const c: Ctx = { q, key: grant.subject, subject, args, opts: { priority: PRIORITY, signal }, now, hurry: deps.hurry ?? new AbortController().signal, spend: deps.spend };
  const answer = await TOOLS[q.tool](c);
  // A draft whose first page can't be answered as read (too large, the grant
  // gone, another account) is not kept; one cut short by the deadline is.
  const refuse = (why: string): never => {
    if (c.keep?.stage === 'draft') drop(c.keep, null);
    throw new Error(why);
  };
  if (bytes(answer) >= MAX_ANSWER_BYTES) refuse(`The answer for ${c.key} is too large to send.`);
  const still = grantFor(q, await grants());
  if (typeof still === 'string') refuse(still);
  // Nothing is awaited from here on: what is checked holds when the snapshots change.
  if (generation !== identityGeneration()) refuse('The signed-in account changed while this was read. Ask again.');
  signal.throwIfAborted();
  const more = (answer as { next_cursor?: unknown }).next_cursor != null;
  if (q.tool !== 'view' && !args.cursor) {
    replace(keyOf(q, grant.subject), args.path, rangeKey(args), c.keep);
    if (c.keep?.stage === 'draft') {
      if (more) c.keep.stage = 'paging';
      else drop(c.keep, null);
    // Already held: another first page answered from the same draft first.
    } else if (c.keep && more && snapshots.get(c.keep.id) !== c.keep) keep(c.keep);
  }
  if (c.hit && !more) c.hit.stage = 'finished';
  return answer;
}

const TOOLS: Record<RelayTool, (c: Ctx) => Promise<unknown>> = { view, comments, files, diff, commits, checks };

const vars = (pr: PullAt, extra: Record<string, unknown> = {}) => ({ owner: pr.owner, repo: pr.repo, number: pr.number, ...extra });

const login = (a: Actor | null) => a?.login ?? null;
const notShown = (c: Ctx) => new Error(`GitHub does not show ${c.key} to this account.`);
/** What every answer names: the subject, and the head the pull request had when read. */
const named = (c: Ctx, head: string | null) => ({ subject: c.key, head_sha: head });
const tokenFor = (c: Ctx, id: string, at: number) => `${CURSOR_TAG[c.q.tool]}.${id}.${at.toString(36)}`;

/**
 * A page of a listing's items (kept as UTF-8 JSON) from the cursor's
 * position: up to `limit`, stopping before PAGE_BYTES (one item at least).
 * `cut`: items follow, but no snapshot (`id` null) keeps them.
 */
function page(c: Ctx, id: string | null, items: Uint8Array[], done = true): { items: unknown[]; total: number | null; next_cursor: string | null; cut: boolean } {
  const from = c.args.cursor?.at ?? 0;
  if (from > items.length) throw new Error(`cursor is not one ${c.q.tool} returned.`);
  const out: unknown[] = [];
  let size = 2;
  for (let i = from; i < items.length && out.length < c.args.limit; i++) {
    const s = items[i].length + 1;
    if (out.length && size + s > PAGE_BYTES) break;
    out.push(JSON.parse(utf8Text.decode(items[i])));
    size += s;
  }
  const next = from + out.length;
  // `done` false: GitHub has more to read into the listing.
  const more = next < items.length || !done;
  return { items: out, total: done ? items.length : null, next_cursor: more && id ? tokenFor(c, id, next) : null, cut: more && !id };
}

/** `truncated`, with the note when a page was cut for want of a snapshot. */
const truncation = (truncated: boolean, cut: boolean) => (cut ? { truncated: true, truncated_note: TOO_LARGE_NOTE } : { truncated });

/** The pull request the query is about, for the reads only a pull request has (its description, timeline, threads, commits listing). */
function pullAt(c: Ctx): PullAt {
  if (c.subject.kind !== 'pull') throw new Error(`${c.key} is not a pull request.`);
  return { owner: c.subject.owner, repo: c.subject.repo, number: c.subject.number };
}

/**
 * The compare the query is about, read once for it (state/compare/fetch.ts
 * `loadCompare`, as its page reads it: by name, every page of one head,
 * through ETag revalidation, and no GraphQL).
 */
function compareOf(c: Ctx): Promise<LoadedCompare> {
  if (c.subject.kind !== 'compare') throw new Error(`${c.key} is not a compare.`);
  c.compare ??= loadCompare(c.subject, c.opts).catch((e: unknown) => {
    throw e instanceof CompareNotFound ? notShown(c) : e;
  });
  return c.compare;
}

/** A branch's history as a query reads it: what the ref is, its tip, and the newest MAX_RELAY_REF_COMMITS commits from it, newest first. */
interface RefRead {
  kind: RefKind;
  tip: string;
  defaultBranch: string | null;
  commits: CompareCommit[];
  /** Older commits exist than those read. */
  more: boolean;
}

/**
 * The branch the query is about, read once for it: the ref exactly as its
 * key names it (`refKind`, one GraphQL request, asked of the budget), then
 * its history from that tip, REF_PAGE commits a request, the newest
 * MAX_RELAY_REF_COMMITS at most (filtered to its path when it has one).
 */
function refOf(c: Ctx): Promise<RefRead> {
  if (c.subject.kind !== 'ref') throw new Error(`${c.key} is not a branch.`);
  const s = c.subject;
  c.ref ??= (async () => {
    await c.spend();
    const { kind, sha: tip, defaultBranch } = await refKind(s, c.opts).catch((e: unknown) => {
      throw e instanceof RefNotFound ? notShown(c) : e;
    });
    const commits: CompareCommit[] = [];
    let more = true;
    for (let page = 1; more && commits.length < MAX_RELAY_REF_COMMITS; page++) {
      const w = await fetchWindow(s, tip, page, c.opts);
      commits.push(...w.commits);
      more = w.more;
    }
    return { kind, tip, defaultBranch, commits: commits.slice(0, MAX_RELAY_REF_COMMITS), more: more || commits.length > MAX_RELAY_REF_COMMITS };
  })();
  return c.ref;
}

/** view: the subject's description and state, a pull request's, a compare's or a branch's. */
async function view(c: Ctx): Promise<unknown> {
  return c.subject.kind === 'pull' ? viewPull(c) : c.subject.kind === 'compare' ? viewCompare(c) : viewRef(c);
}

/**
 * A branch's view: what the ref is (a branch, a tag or a commit, and
 * whether it is the default branch), its tip, the path its history is
 * filtered to, its latest commit (touching that path), how many commits a
 * query reads of it and whether there are older ones, and the open pull
 * requests from the branch. GraphQL only to read the ref.
 */
async function viewRef(c: Ctx): Promise<unknown> {
  const s = c.subject as RefSubject;
  const r = await refOf(c);
  const latest = r.commits[0] ?? null;
  const pulls = r.kind === 'branch' ? await pullsFrom(s.owner, s.repo, s.owner, s.ref, c.opts) : [];
  const web = session.config?.github_web_url ?? 'https://github.com';
  return {
    ...named(c, r.tip),
    url: `${web}/${s.owner}/${s.repo}/commits/${encodeURIComponent(s.ref).replace(/%2F/gi, '/')}${s.path === null ? '' : '/' + encodeURIComponent(s.path).replace(/%2F/gi, '/')}`,
    ref: { untrusted_name: s.ref, kind: r.kind, sha: r.tip, default_branch: r.kind === 'branch' && r.defaultBranch === s.ref },
    untrusted_path: s.path,
    latest: latest && { sha: latest.sha, untrusted_message: latest.message, author: { untrusted_name: latest.authorName, login: latest.author !== latest.authorName ? latest.author : null }, committed: latest.committed },
    commits_read: r.commits.length,
    older_commits: r.more,
    open_pull_requests: pulls.map((p) => ({ number: p.number, untrusted_title: p.title, untrusted_base: p.base })),
  };
}

/**
 * A compare's view: its ends (the base branch and its tip, the head branch,
 * its fork and SHA), the merge base, how far the head is ahead and behind,
 * its commit and file counts and line totals, and the open pull requests
 * from the head. No GraphQL: the compare and the pull requests are REST.
 */
async function viewCompare(c: Ctx): Promise<unknown> {
  const s = c.subject as CompareSubject;
  const cmp = await compareOf(c);
  const pulls = await headPulls(s, cmp.baseRef, c.opts);
  return {
    ...named(c, cmp.head),
    url: `${session.config?.github_web_url ?? 'https://github.com'}/${s.owner}/${s.repo}/compare/${compareSpec(s, cmp.baseRef)}`,
    base: { untrusted_ref: cmp.baseRef, sha: cmp.baseSha, default_branch: s.base === null },
    // Where the head lives, only as the subject names it: a fork's owner, and its repository's name or null (GitHub finds the owner's fork in the network, whatever it is named).
    head: { untrusted_ref: s.head, sha: cmp.head, owner: s.headRepo?.owner ?? s.owner, repo: s.headRepo ? s.headRepo.repo : s.repo },
    merge_base: cmp.mergeBase,
    status: cmp.status,
    ahead_by: cmp.aheadBy,
    behind_by: cmp.behindBy,
    total_commits: cmp.totalCommits,
    changed_files: cmp.files.length,
    files_truncated: cmp.filesMissing === null,
    additions: cmp.totals.added,
    deletions: cmp.totals.removed,
    open_pull_requests: pulls.map((p) => ({ number: p.number, untrusted_title: p.title, untrusted_base: p.base })),
  };
}

/**
 * A pull request's view: its description and state. PR_HEADER_QUERY, the
 * page's own read of its header, is the only query holding these fields
 * (no lighter one names the body, labels and merge state); none of its
 * activity is read.
 */
async function viewPull(c: Ctx): Promise<unknown> {
  await c.spend();
  const res = readPrHeader(await graphql<unknown>(PR_HEADER_QUERY, vars(pullAt(c)), c.opts));
  const p = res.repository?.pullRequest;
  if (!res.repository || !p) throw notShown(c);
  return {
    ...named(c, p.headRefOid),
    url: p.url,
    untrusted_title: p.title,
    untrusted_body: p.body,
    state: p.state,
    draft: p.isDraft,
    merged: p.merged,
    author: login(p.author),
    author_association: p.authorAssociation,
    base: { untrusted_ref: p.baseRefName, sha: p.baseRefOid },
    head: { untrusted_ref: p.headRefName, sha: p.headRefOid, repo: p.headRepository?.nameWithOwner ?? null },
    labels: p.labels.nodes.map((l) => ({ untrusted_name: l.name, untrusted_description: l.description })),
    labels_total: p.labels.totalCount,
    created: p.createdAt,
    updated: p.updatedAt,
    mergeable: p.mergeable,
    merge_state: p.mergeStateStatus,
    review_decision: p.reviewDecision,
    additions: p.additions,
    deletions: p.deletions,
    changed_files: p.changedFiles,
  };
}

/** One comment of comments: on the conversation, a review's summary, or in a review thread. */
type CommentItem =
  | { kind: 'comment'; id: string; author: string | null; created: string; url: string; untrusted_body: string }
  | { kind: 'review'; id: string; author: string | null; state: string; created: string; url: string; untrusted_body: string }
  | {
      kind: 'thread_comment';
      id: string;
      author: string | null;
      created: string;
      url: string;
      untrusted_body: string;
      thread: { id: string; untrusted_path: string; line: number | null; start_line: number | null; original_line: number | null; side: string; resolved: boolean; outdated: boolean };
    };

/** What a review says on its own: a summary, or a verdict. A COMMENTED review without one only holds thread comments. */
const reviewShown = (r: Extract<TimelineItem, { __typename: 'PullRequestReview' }>) =>
  r.state !== 'PENDING' && (r.body.trim() !== '' || r.state !== 'COMMENTED');

/** Where a CommentsFeed reads next: the timeline's older pages, the threads' pages, a long thread's further comments, or nothing left. */
type Next =
  | { at: 'timeline'; before: string | null; pages: number }
  | { at: 'threads'; after: string | null; pages: number }
  | { at: 'comments'; thread: ReviewThread; comments: ThreadComment[]; after: string; pages: number; rest: ReviewThread[]; threads: Next }
  | { at: 'end' };

/**
 * comments' snapshot, read into a GitHub page at a time (`step`): the
 * items so far as UTF-8 JSON, in listing order, and where it reads next.
 * Its reads have their own signal, so one under way when a page is
 * answered goes on into it; dropping the snapshot aborts them.
 */
class CommentsFeed {
  items: Uint8Array[] = [];
  /** The pull request's head when its first page was read; undefined until then. */
  head: string | null | undefined;
  next: Next = { at: 'timeline', before: null, pages: 0 };
  conversation_truncated = false;
  threads_truncated = false;
  /** Grew past entryBytes: read no further. */
  cut = false;
  readonly abort = new AbortController();
  /** The read under way, which another page waits on instead of reading the same page. */
  private reading: Promise<void> | null = null;
  get done(): boolean { return this.next.at === 'end'; }
  get bytes(): number { return this.items.reduce((n, b) => n + b.length, 0) + bytes(this.meta()); }
  meta() { return { conversation_truncated: this.conversation_truncated, threads_truncated: this.threads_truncated }; }

  /** Reads the next GitHub page into the feed, or waits for the read under way. */
  step(c: Ctx, s: Snapshot): Promise<void> {
    if (!this.reading) {
      const p = (this.reading = this.read(c, s).finally(() => (this.reading = null)));
      // A page that stopped waiting for it (hurried, aborted) leaves its failure to the next page's own read.
      p.catch(() => {});
    }
    return this.reading;
  }

  private async read(c: Ctx, s: Snapshot): Promise<void> {
    const n = this.next;
    if (n.at === 'end') return;
    await c.spend();
    const opts = { priority: c.opts.priority, signal: this.abort.signal };
    const ask = (doc: string, v: Record<string, unknown>) => graphql<unknown>(doc, v, opts);
    // Kept only while the snapshot is: dropped meanwhile (revoked, replaced, another account), what came is not.
    const kept = () => snapshots.get(s.id) === s && !this.abort.signal.aborted;
    if (n.at === 'timeline') {
      const got = readTimelinePage(await ask(PR_TIMELINE_PAGE, vars(pullAt(c), { before: n.before })));
      if (!kept()) return;
      if (!got) {
        if (n.before === null) throw notShown(c);
        this.conversation_truncated = true;
        this.next = { at: 'threads', after: null, pages: 0 };
      } else {
        // Newest first: each older page goes after the ones read.
        for (const t of [...got.nodes].reverse()) {
          if (t.__typename === 'IssueComment') this.push({ kind: 'comment', id: t.id, author: login(t.author), created: t.createdAt, url: t.url, untrusted_body: t.body });
          else if (t.__typename === 'PullRequestReview' && reviewShown(t)) this.push({ kind: 'review', id: t.id, author: login(t.author), state: t.state, created: t.submittedAt ?? t.createdAt, url: t.url, untrusted_body: t.body });
        }
        const on = got.pageInfo.hasPreviousPage && advances(got.pageInfo.startCursor, n.before) && n.pages < MAX_TIMELINE_PAGES;
        if (got.pageInfo.hasPreviousPage && !on) this.conversation_truncated = true;
        this.next = on ? { at: 'timeline', before: got.pageInfo.startCursor, pages: n.pages + 1 } : { at: 'threads', after: null, pages: 0 };
      }
    } else if (n.at === 'threads') {
      const got = readThreadsPage(await ask(PR_THREADS_PAGE, vars(pullAt(c), { after: n.after })));
      if (!kept()) return;
      if (!got) {
        if (n.after === null) throw notShown(c);
        this.threads_truncated = true;
        this.next = { at: 'end' };
      } else {
        const on = got.pageInfo.hasNextPage && advances(got.pageInfo.endCursor, n.after) && n.pages < MAX_THREAD_PAGES;
        if (got.pageInfo.hasNextPage && !on) this.threads_truncated = true;
        this.threadsFrom(got.nodes, on ? { at: 'threads', after: got.pageInfo.endCursor, pages: n.pages + 1 } : { at: 'end' });
      }
    } else {
      const got = readCommentsPage(await ask(THREAD_COMMENTS_PAGE, { id: n.thread.id, after: n.after }));
      if (!kept()) return;
      const comments = got ? n.comments.concat(got.nodes) : n.comments;
      const on = !!got?.pageInfo.hasNextPage && advances(got.pageInfo.endCursor, n.after) && n.pages < MAX_COMMENT_PAGES;
      if (on) this.next = { ...n, comments, after: got!.pageInfo.endCursor!, pages: n.pages + 1 };
      else {
        this.thread(n.thread, comments);
        this.threadsFrom(n.rest, n.threads);
      }
    }
    // Counted as it grows; past entryBytes it reads no further, and says so at its end.
    const before = s.bytes;
    s.bytes = this.bytes;
    held += s.bytes - before;
    if (s.bytes > SNAPSHOT_LIMITS.entryBytes) { this.cut = true; this.next = { at: 'end' }; }
    room(s);
  }

  /** Puts `threads` in, in order, until one has further comments to read; `then`, once all are in. */
  private threadsFrom(threads: ReviewThread[], then: Next) {
    for (let i = 0; i < threads.length; i++) {
      const t = threads[i];
      const more = t.comments.pageInfo;
      if (more?.hasNextPage && advances(more.endCursor, null)) {
        this.next = { at: 'comments', thread: t, comments: t.comments.nodes, after: more.endCursor, pages: 0, rest: threads.slice(i + 1), threads: then };
        return;
      }
      this.thread(t, t.comments.nodes);
    }
    this.next = then;
  }

  private thread(th: ReviewThread, comments: ThreadComment[]) {
    const thread = { id: th.id, untrusted_path: th.path, line: th.line, start_line: th.startLine, original_line: th.originalLine, side: th.diffSide, resolved: th.isResolved, outdated: th.isOutdated };
    for (const cm of comments) {
      if (cm.pullRequestReview?.state === 'PENDING') continue;
      this.push({ kind: 'thread_comment', id: cm.id, author: login(cm.author), created: cm.createdAt, url: cm.url, untrusted_body: cm.body, thread });
    }
  }

  private push(item: CommentItem) { this.items.push(utf8.encode(JSON.stringify(item))); }
}

/** Resolves once `signal` aborts (never, if it doesn't). */
const aborted = (signal: AbortSignal) => new Promise<void>((resolve) => (signal.aborted ? resolve() : signal.addEventListener('abort', () => resolve(), { once: true })));

/**
 * The comments snapshot this query reads: its cursor's, or for a first
 * page, the draft a first page asked before left, else a new draft.
 */
function feedFor(c: Ctx): Snapshot {
  if (c.args.cursor) {
    const s = lookup(c, c.args.cursor.id);
    c.hit = s;
    return s;
  }
  const key = keyOf(c.q, c.key);
  let s = [...snapshots.values()].find((x) => x.key === key && x.stage === 'draft');
  if (s) {
    s.used = c.now();
    snapshots.delete(s.id);
    snapshots.set(s.id, s);
  } else {
    const now = c.now();
    const feed = new CommentsFeed();
    s = { id: newId(), connection: c.q.connection, subject: matchKey(c.key), key, path: null, range: null, data: feed, bytes: feed.bytes, created: now, used: now, stage: 'draft' };
    keep(s);
  }
  c.keep = s;
  return s;
}

/**
 * comments: the conversation's comments and reviews, newest first
 * (`conversation_truncated`: older timeline pages past the paging cap were
 * left out), then every review thread's comments in thread order
 * (`threads_truncated`: threads past the paging cap were left out), each
 * as far as read so far. `total` is null until the whole listing is read.
 * The person's own pending (unpublished) comments are left out.
 */
async function comments(c: Ctx): Promise<unknown> {
  const s = feedFor(c);
  const feed = s.data as CommentsFeed;
  // The head the listing is of: the pull request's REST resource, revalidated by its ETag (no GraphQL point).
  feed.head ??= await pullHead(c);
  const from = c.args.cursor?.at ?? 0;
  if (from > feed.items.length) throw new Error(`cursor is not one ${c.q.tool} returned.`);
  const stop = Promise.race([aborted(c.hurry), aborted(c.opts.signal)]);
  while (feed.items.length < from + c.args.limit && !feed.done && !c.hurry.aborted) {
    c.opts.signal.throwIfAborted();
    try {
      await Promise.race([feed.step(c, s), stop]);
    } catch (e) {
      // Out of budget: the page goes with what has been read, if anything.
      if (e instanceof BudgetSpent && feed.items.length > from) break;
      throw e;
    }
  }
  c.opts.signal.throwIfAborted();
  const { cut, ...p } = page(c, s.id, feed.items, feed.done);
  const { conversation_truncated, threads_truncated } = feed.meta();
  return { ...named(c, feed.head ?? null), ...p, conversation_truncated, threads_truncated, ...truncation(conversation_truncated || threads_truncated, cut || (feed.cut && p.next_cursor === null)) };
}

/** The pull request's REST resource (ETag-cached): its head and base. */
async function pull(c: Ctx): Promise<{ head: string | null; base: string | null; changed: number | null }> {
  const p = await rest<{ head?: { sha?: unknown }; base?: { sha?: unknown }; changed_files?: unknown }>(`repos/${c.subject.owner}/${c.subject.repo}/pulls/${pullAt(c).number}`, c.opts);
  const sha = (v: unknown) => (typeof v === 'string' ? v : null);
  return { head: sha(p?.head?.sha), base: sha(p?.base?.sha), changed: typeof p?.changed_files === 'number' ? p.changed_files : null };
}
const pullHead = async (c: Ctx) => (await pull(c)).head;

/** A commit of the pull request, as GitHub's REST listing gives it. */
interface RestCommit {
  sha: string;
  parents?: { sha: string }[];
  commit?: { message?: string; author?: { name?: string; date?: string } | null; committer?: { name?: string; date?: string } | null };
  author?: { login?: string } | null;
}
/** GitHub lists at most this many of a pull request's commits. */
const MAX_COMMITS = 250;
/**
 * The pull request's track, as far as a range can name it: its merge base
 * (the first point), then its current commits in GitHub's order (by date),
 * the head among them. `truncated` when GitHub listed fewer than all of
 * them: it lists MAX_COMMITS at most, the oldest, so on a longer pull
 * request the head, when not listed, is put after them without its
 * commit's metadata, and the commits between are not on the track.
 */
interface Track { head: string; base: string; commits: RestCommit[]; truncated: boolean }
/**
 * Reads the track. The commit listing names no head and is read a page at
 * a time, so the pull request's head is read before and after it (its REST
 * resource, ETag-revalidated, as fetchFiles does): a listing the head moved
 * under is read again, FILES_TRIES times at most, then it fails rather than
 * mix two heads' commits.
 */
async function trackOf(c: Ctx): Promise<Track> {
  if (c.subject.kind === 'ref') {
    // A branch's: its page's main lane over the newest MAX_RELAY_REF_COMMITS commits (logic/refTrack.ts
    // `historyLane`: the first-parent chain, or a path's listing), so an agent diffs what the person does.
    const r = await refOf(c);
    const { lane, base: laneBase, cut } = historyLane(r.commits, c.subject.path !== null);
    if (!lane.length) throw new Error(`${c.key} has no commits${c.subject.path === null ? '' : ' touching its path'}.`);
    const listed: RestCommit[] = lane.map((x) => ({
      sha: x.sha,
      parents: x.parents.map((p) => ({ sha: p })),
      commit: { message: x.message, author: { name: x.authorName ?? undefined, date: x.authored ?? undefined }, committer: { date: x.committed ?? undefined } },
      author: x.author && x.author !== x.authorName ? { login: x.author } : null,
    }));
    return { head: listed[listed.length - 1].sha, base: laneBase.toLowerCase(), commits: listed, truncated: r.more || cut };
  }
  if (c.subject.kind === 'compare') {
    // A compare's: the merge base, then the commits the compare holds (the newest MAX_COMPARE_COMMITS), the head last.
    const cmp = await compareOf(c);
    const listed: RestCommit[] = cmp.commits.map((x) => ({
      sha: x.sha,
      parents: x.parents.map((p) => ({ sha: p })),
      commit: { message: x.message, author: { name: x.authorName ?? undefined, date: x.authored ?? undefined }, committer: { date: x.committed ?? undefined } },
      author: x.author && x.author !== x.authorName ? { login: x.author } : null,
    }));
    const at = cmp.head.toLowerCase();
    const headListed = cmp.head === cmp.mergeBase || listed.some((x) => x.sha.toLowerCase() === at);
    return { head: cmp.head, base: cmp.mergeBase.toLowerCase(), commits: headListed ? listed : [...listed, { sha: cmp.head }], truncated: cmp.truncated };
  }
  let { head, base } = await pull(c);
  for (let tries = 1; ; tries++) {
    if (!head || !base) throw notShown(c);
    const list = await restAll<RestCommit>(`repos/${c.subject.owner}/${c.subject.repo}/pulls/${pullAt(c).number}/commits?per_page=100`, { ...c.opts, maxPages: Math.ceil(MAX_COMMITS / 100) });
    const after = await pull(c);
    if (after.head === head && after.base === base) {
      const listed = list.filter((x) => typeof x?.sha === 'string');
      // GitHub documents the listing as 250 commits at most, not as ending at the head (it is ordered by date).
      const at = head.toLowerCase();
      const headListed = listed.some((x) => x.sha.toLowerCase() === at);
      const commits = headListed ? listed : [...listed, { sha: head }];
      // As the Files view finds a snapshot's base (state/diffs.svelte.ts `getMergeBase`).
      const cmp = await rest<{ merge_base_commit?: { sha?: unknown } }>(`repos/${c.subject.owner}/${c.subject.repo}/compare/${base}...${head}?per_page=1`, c.opts);
      const mergeBase = cmp?.merge_base_commit?.sha;
      if (typeof mergeBase !== 'string') throw new Error(`GitHub did not say where ${c.key} branches from.`);
      return { head, base: mergeBase, commits, truncated: !headListed || listed.length >= MAX_COMMITS };
    }
    if (tries >= FILES_TRIES) throw new Error(`${c.key} kept changing while its commits were listed. Try again in a moment.`);
    ({ head, base } = after);
  }
}
/** The points of `t`, in order: its merge base, then its commits, the head last. */
const pointsOf = (t: Track) => [t.base, ...t.commits.map((x) => x.sha.toLowerCase())];
/** Throws unless `sha` is a point of `t`. */
function onTrack(c: Ctx, t: Track, sha: string, end: 'from' | 'to'): number {
  const at = pointsOf(t).indexOf(sha);
  if (at < 0)
    throw new Error(
      c.subject.kind === 'ref'
        ? `${end} ${sha} is not on ${c.key}'s track: name its base (${t.base}) or one of the newest ${MAX_RELAY_REF_COMMITS} commits of its history (see commits).`
        : `${end} ${sha} is not on ${c.key}'s track: name its merge base (${t.base}) or one of its current commits (see commits).`,
    );
  return at;
}
/** The range the query asks for, checked against the track: null when it names none, the whole subject at its head. */
async function rangeOf(c: Ctx): Promise<{ from: string; to: string; track: Track } | null> {
  if (c.args.from === null && c.args.to === null) return null;
  const track = await trackOf(c);
  const from = c.args.from ?? track.base, to = c.args.to ?? track.head.toLowerCase();
  if (onTrack(c, track, from, 'from') >= onTrack(c, track, to, 'to')) throw new Error(`from must come before to on ${c.key}'s track.`);
  return { from, to, track };
}
/** The files changed from `from` to `to` (GitHub's compare, which lists 300 at most). */
async function comparedFiles(c: Ctx, from: string, to: string): Promise<{ files: PrFile[]; truncated: boolean }> {
  const cmp = await rest<{ files?: PrFile[] }>(`repos/${c.subject.owner}/${c.subject.repo}/compare/${from}...${to}`, c.opts);
  const files = (cmp?.files ?? []).filter((f) => typeof f?.filename === 'string');
  return { files, truncated: files.length >= 300 };
}

/** The changed files (pulls/:n/files, ETag-cached, as the Files view lists them), with the head they are of. */
async function changedFiles(c: Ctx): Promise<{ head: string | null; files: PrFile[] }> {
  const { head, files } = await fetchFiles(pullAt(c), c.opts.signal, undefined, c.opts.priority);
  return { head, files: files.filter((f) => typeof f?.filename === 'string') };
}
/**
 * The files the query is about: the pull request's changed files, or those
 * changed over its range, with what the answer names and says of them:
 * the head, the range's ends, GitHub's count (null when it gave none) and
 * whether GitHub changed more than it listed.
 */
async function filesOf(c: Ctx): Promise<{ files: PrFile[]; meta: { head: string | null; from?: string; to?: string; changed: number | null; truncated: boolean } }> {
  const range = await rangeOf(c);
  if (c.subject.kind === 'ref') {
    // A branch's, from its base (or the range's from) to its head (or the range's to), in its path: the empty tree's are the head's tree.
    const track = range?.track ?? (await trackOf(c));
    const from = range?.from ?? track.base, to = range?.to ?? track.head.toLowerCase();
    const w = await windowFiles(c.subject, from, to, c.opts);
    return { files: w.files, meta: { head: (await refOf(c)).tip, ...(range ? { from, to } : {}), changed: null, truncated: w.missing === null } };
  }
  if (range) {
    const { files, truncated } = await comparedFiles(c, range.from, range.to);
    return { files, meta: { head: range.track.head, from: range.from, to: range.to, changed: null, truncated } };
  }
  if (c.subject.kind === 'compare') {
    // Its files, GitHub's 300 and the rest from the trees; `truncated` when a tree was too large to list the rest.
    const cmp = await compareOf(c);
    return { files: cmp.files, meta: { head: cmp.head, changed: null, truncated: cmp.filesMissing === null } };
  }
  const { head, files } = await changedFiles(c);
  // The pull request's REST resource, which the listing just revalidated: no further request is counted.
  const { changed } = await pull(c);
  return { files, meta: { head, changed, truncated: changed === null ? files.length >= MAX_FILES : files.length < changed } };
}
/** What a range answer adds: its ends. */
const ends = (m: { from?: string; to?: string }) => (m.from === undefined ? {} : { from: m.from, to: m.to });

type FilesData = Listing<{ head: string | null; from?: string; to?: string; changed_files: number | null; truncated: boolean }>;

/** A changed file's blob at the head, as the Files view keys its figures (null for a deleted file). */
const newSha = (f: PrFile): string | null => (f.status === 'removed' ? null : (f.sha ?? null));

/**
 * files: the changed files, or those changed over the range, a page at a
 * time. `truncated` says GitHub changed more than were listed (it lists
 * MAX_FILES of a pull request at most, 300 of a range); `changed_files` is
 * GitHub's count of the pull request's, null for a range or when it gave
 * none. Each file's `think` is its change blocks' complexity summed
 * (state/complexityRegistry), with `think_basis` saying whose: `view` (the
 * person's open Files view, both figures) or `patch` (an estimate from
 * GitHub's patch); null when neither could score it.
 */
async function files(c: Ctx): Promise<unknown> {
  const { id, data } = await snapshot<FilesData>(c, async () => {
    const { files: list, meta } = await filesOf(c);
    // Each file's complexity: the person's open view's figures, else the patch's estimate.
    const think = await Promise.all(list.map((f) => fileComplexity(c.subject, f.filename, newSha(f), f.patch ?? null).catch(() => null)));
    const items = list.map((f, i) => ({
      untrusted_path: f.filename,
      untrusted_previous_path: f.previous_filename ?? null,
      status: f.status,
      additions: f.additions,
      deletions: f.deletions,
      think: think[i]?.think ?? null,
      think_basis: think[i]?.basis ?? null,
    }));
    return listing(items, { head: meta.head, ...ends(meta), changed_files: meta.changed, truncated: meta.truncated });
  }, listingBytes);
  const { items, cut, ...more } = page(c, id, data.items);
  return { ...named(c, data.meta.head), ...ends(data.meta), files: items, ...more, changed_files: data.meta.changed_files, ...truncation(data.meta.truncated, cut) };
}

/** Git's blob ID for an empty file. */
const EMPTY_BLOB = 'e69de29bb2d1d6434b8b29ae775ad8c2e48c5391';
const ESCAPES: Record<number, string> = { 7: '\\a', 8: '\\b', 9: '\\t', 10: '\\n', 11: '\\v', 12: '\\f', 13: '\\r', 0x22: '\\"', 0x5c: '\\\\' };

/**
 * A path as git writes it in a diff: as it is, or C-quoted when it holds
 * a quote, a backslash, a control character or a byte outside ASCII
 * (each such byte in octal, as git's default `core.quotePath` does).
 */
export function gitQuote(path: string): string {
  let out = '';
  let quoted = false;
  for (const b of utf8.encode(path)) {
    if (b < 0x20 || b >= 0x7f || b === 0x22 || b === 0x5c) {
      quoted = true;
      out += ESCAPES[b] ?? '\\' + b.toString(8).padStart(3, '0');
    } else {
      out += String.fromCharCode(b);
    }
  }
  return quoted ? `"${out}"` : path;
}

/**
 * One file's part of a unified diff, from GitHub's per-file patch, with
 * git's header lines. GitHub's listing names no file mode (100644 is
 * written for a new or deleted file) and no similarity index (none is
 * written). A file GitHub gives no patch for and no changed lines is a
 * pure rename or copy, an empty file added or deleted, a mode change
 * (`changed`, `unchanged`), or binary; a renamed binary file whose content
 * also changed reads as a pure rename, since GitHub says the same of both.
 */
export function fileDiff(f: PrFile): string {
  const from = f.previous_filename ?? f.filename;
  const to = f.filename;
  const lines = [`diff --git ${gitQuote(`a/${from}`)} ${gitQuote(`b/${to}`)}`];
  if (f.status === 'added') lines.push('new file mode 100644');
  if (f.status === 'removed') lines.push('deleted file mode 100644');
  if (f.status === 'renamed') lines.push(`rename from ${gitQuote(from)}`, `rename to ${gitQuote(to)}`);
  if (f.status === 'copied') lines.push(`copy from ${gitQuote(from)}`, `copy to ${gitQuote(to)}`);
  const a = f.status === 'added' ? '/dev/null' : gitQuote(`a/${from}`);
  const b = f.status === 'removed' ? '/dev/null' : gitQuote(`b/${to}`);
  if (f.patch) {
    lines.push(`--- ${a}`, `+++ ${b}`, f.patch);
  } else if (f.changes > 0) {
    lines.push(`# GitHub gave no patch for this file (too large): +${f.additions} -${f.deletions}`);
  } else if (f.status === 'changed' || f.status === 'unchanged') {
    lines.push('# mode changed (GitHub gives no modes)');
  } else {
    const headerOnly = f.status === 'renamed' || f.status === 'copied' || ((f.status === 'added' || f.status === 'removed') && f.sha === EMPTY_BLOB);
    if (!headerOnly) lines.push(`Binary files ${a} and ${b} differ`);
  }
  return lines.join('\n') + '\n';
}

/** diff's snapshot: the unified diff, and each scored file's complexity with the byte offset its diff starts at; what the answer names. */
interface DiffData {
  bytes: Uint8Array;
  files: { at: number; entry: { untrusted_path: string } & AgentFileComplexity }[];
  head: string | null;
  from?: string;
  to?: string;
}

/**
 * diff: the unified diff of `path` (its current or previous name), or of
 * every file, of the pull request or its range. Each part carries `complexity`: for each file whose diff
 * starts in it, its change blocks' complexity (as files' `think`, block
 * by block: line ranges, both figures, band, reasons and risk flags). The first call reads GitHub; each `next_cursor` names the
 * next chunk of the same snapshot. A chunk is DIFF_BYTES at most, ending at
 * a line break where one falls in it, smaller where JSON would escape it
 * past PAGE_BYTES.
 */
async function diff(c: Ctx): Promise<unknown> {
  const { id, data, path } = await snapshot<DiffData>(c, async () => {
    const { files: all, meta } = await filesOf(c);
    let list = all;
    const want = c.args.path;
    if (want !== null) {
      list = list.filter((f) => f.filename === want || f.previous_filename === want);
      if (!list.length) throw new Error(`${c.key} changes no file at ${JSON.stringify(want)}${meta.from === undefined ? '' : ' in that range'}.`);
    }
    const parts = list.map((f) => utf8.encode(fileDiff(f)));
    const scored = await Promise.all(list.map((f) => fileComplexity(c.subject, f.filename, newSha(f), f.patch ?? null).catch(() => null)));
    const files: DiffData['files'] = [];
    let at = 0;
    list.forEach((f, i) => {
      const x = scored[i];
      if (x) files.push({ at, entry: { untrusted_path: f.filename, basis: x.basis, think: x.think, blocks: x.blocks } });
      at += parts[i].length;
    });
    const bytes = new Uint8Array(at);
    let o = 0;
    for (const p of parts) {
      bytes.set(p, o);
      o += p.length;
    }
    return { bytes, files, head: meta.head, ...ends(meta) };
  }, (d) => d.bytes.length + JSON.stringify(d.files).length);
  const all = data.bytes;
  const start = c.args.cursor?.at ?? 0;
  if (start > all.length || (start < all.length && (all[start] & 0xc0) === 0x80)) throw new Error('cursor is not one diff returned.');
  let end = Math.min(start + DIFF_BYTES, all.length);
  for (;;) {
    if (end < all.length) {
      const nl = all.lastIndexOf(0x0a, end - 1);
      if (nl >= start) end = nl + 1;
      else while (end > start + 1 && (all[end] & 0xc0) === 0x80) end--;
    }
    const text = utf8Text.decode(all.subarray(start, end));
    const more = end < all.length;
    const answer = {
      ...named(c, data.head),
      ...ends(data),
      path,
      offset: start,
      untrusted_diff: text,
      // The complexity of the files whose diff starts in this part.
      complexity: data.files.filter((f) => f.at >= start && f.at < end).map((f) => f.entry),
      total_bytes: all.length,
      next_cursor: more && id ? tokenFor(c, id, end) : null,
      ...(more && !id ? { truncated: true, truncated_note: TOO_LARGE_NOTE } : { truncated: more }),
    };
    if (bytes(answer) < PAGE_BYTES || end - start <= 1) return answer;
    end = start + Math.floor((end - start) / 2);
  }
}

/** One check of checks: a check run, or a commit status. */
function check(x: CheckContext): unknown {
  if (x.__typename === 'StatusContext') {
    return { type: 'status', untrusted_context: x.context ?? null, state: x.state ?? null, untrusted_url: x.targetUrl ?? null, untrusted_description: x.description ?? null };
  }
  return {
    type: 'check_run',
    untrusted_name: x.name ?? null,
    untrusted_workflow: x.checkSuite?.workflowRun?.workflow.name ?? null,
    untrusted_app: x.checkSuite?.app?.name ?? null,
    status: x.status ?? null,
    conclusion: x.conclusion ?? null,
    untrusted_url: x.detailsUrl ?? null,
    started: x.startedAt ?? null,
    completed: x.completedAt ?? null,
  };
}

type ChecksData = Listing<{ head: string | null; commit: string; state: string | null; truncated: boolean }>;

/** A commit's checks other than a pull request's head's (`commitRollup`, COMMIT_CHECKS), each page asked of the budget. */
async function commitChecks(c: Ctx, oid: string): Promise<{ state: string | null; contexts: CheckContexts | null }> {
  const got = await commitRollup(c.subject.owner, c.subject.repo, oid, c.opts, () => c.spend());
  if (!got) throw new Error(`GitHub does not show commit ${oid} of ${c.key} to this account.`);
  return got;
}

/**
 * checks: the check runs and statuses of the head commit (PR_CHECKS, paged
 * as the page pages them), or of `to`, a point on the track
 * (COMMIT_CHECKS), a page at a time. `commit` is the commit read;
 * `truncated` says GitHub has more than were read (the paging cap, or a
 * head that moved meanwhile).
 */
async function checks(c: Ctx): Promise<unknown> {
  const { id, data } = await snapshot<ChecksData>(c, async () => {
    if (c.args.to !== null) {
      const track = await trackOf(c);
      onTrack(c, track, c.args.to, 'to');
      if (c.args.to !== track.head.toLowerCase()) {
        const { state, contexts } = await commitChecks(c, c.args.to);
        return listing((contexts?.nodes ?? []).map(check), { head: await headOf(c, track), commit: c.args.to, state, truncated: !!contexts?.pageInfo?.hasNextPage });
      }
    }
    if (c.subject.kind === 'compare' || c.subject.kind === 'ref') {
      // A compare's head, and a branch's newest commit (in its path), is a commit like any other: its checks are that commit's.
      const commit = c.subject.kind === 'compare' ? (await compareOf(c)).head : (await trackOf(c)).head;
      const { state, contexts } = await commitChecks(c, commit);
      return listing((contexts?.nodes ?? []).map(check), { head: c.subject.kind === 'ref' ? (await refOf(c)).tip : commit, commit, state, truncated: !!contexts?.pageInfo?.hasNextPage });
    }
    const at = pullAt(c);
    await c.spend();
    const first = readChecks(await graphql<unknown>(PR_CHECKS, vars(at, { after: null }), c.opts));
    if (!first) throw notShown(c);
    const rollup = first.commits.nodes[0]?.commit.statusCheckRollup ?? null;
    // Told before each further page, which counts against the budget too.
    const contexts = rollup ? await allContexts(at, rollup.contexts, first.headRefOid, c.opts.priority, c.opts.signal, () => c.spend()) : null;
    return listing((contexts?.nodes ?? []).map(check), { head: first.headRefOid, commit: first.headRefOid, state: rollup?.state ?? null, truncated: !!contexts?.pageInfo?.hasNextPage });
  }, listingBytes);
  const { items, cut, ...more } = page(c, id, data.items);
  const { head, commit, state, truncated } = data.meta;
  return { ...named(c, head), commit, state, checks: items, ...more, ...truncation(truncated, cut) };
}

/** A force-push of a branch, as `commits` names it. */
interface PushItem { before: string; after: string; actor: string | null; at: string | null }
type CommitsData = Listing<{ head: string; from?: string; to?: string; truncated: boolean; force_pushes?: PushItem[] | null }>;

/** What a branch's `commits` says of its force-pushes. */
const PUSHES_NOTE = 'As far back as GitHub keeps the branch’s activity, newest first; Changeling keeps none of its own.';

/** The head SHA an answer names: a branch's tip (its track ends at its newest commit in its path), else the track's head. */
async function headOf(c: Ctx, track: Track): Promise<string> {
  return c.subject.kind === 'ref' ? (await refOf(c)).tip : track.head;
}

/**
 * commits: the subject's commits, oldest first (a pull request's current
 * ones; a compare's it holds; a branch's newest MAX_RELAY_REF_COMMITS), or
 * those after `from` up to and including `to`, a page at a time: each
 * with its SHA, parents, message, author and times. `truncated` says
 * fewer than all were listed (a pull request's MAX_COMMITS at most, the
 * oldest: the head then follows them with its SHA alone; a branch's older
 * history). A branch's names its newest force-pushes too
 * (`force_pushes`, null when GitHub's activity could not be read).
 */
async function commits(c: Ctx): Promise<unknown> {
  const { id, data } = await snapshot<CommitsData>(c, async () => {
    const range = await rangeOf(c);
    const track = range?.track ?? (await trackOf(c));
    const points = pointsOf(track);
    // Commit i is point i + 1: the merge base comes first.
    const from = range ? points.indexOf(range.from) : 0, to = range ? points.indexOf(range.to) : points.length - 1;
    const items = track.commits.slice(from, to).map((x) => ({
      sha: x.sha,
      parents: (x.parents ?? []).map((p) => p.sha),
      untrusted_message: x.commit?.message ?? null,
      author: { untrusted_name: x.commit?.author?.name ?? null, login: x.author?.login ?? null },
      authored: x.commit?.author?.date ?? null,
      committed: x.commit?.committer?.date ?? null,
    }));
    const s = c.subject;
    const pushes = s.kind === 'ref' && (await refOf(c)).kind === 'branch' ? await fetchForcePushes(s.owner, s.repo, s.ref, c.opts).catch(() => null) : undefined;
    return listing(items, { head: await headOf(c, track), ...(range ? { from: range.from, to: range.to } : {}), truncated: track.truncated, ...(pushes === undefined ? {} : { force_pushes: pushes }) });
  }, listingBytes);
  const { items, cut, ...more } = page(c, id, data.items);
  const { force_pushes } = data.meta;
  return {
    ...named(c, data.meta.head),
    ...ends(data.meta),
    commits: items,
    ...more,
    ...truncation(data.meta.truncated, cut),
    ...(force_pushes === undefined ? {} : { force_pushes, force_pushes_note: PUSHES_NOTE }),
  };
}
